import { Loading } from "../app/Status";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import {
  ALWAYS_EDIT_ROLES,
  MODULE_LABELS,
  MODULE_LEVEL_LABELS,
  MODULE_LEVELS,
  ORG_MODULE_STATUS_LABELS,
  ROLE_LABELS,
  ROLES,
  type ModuleKey,
  type ModuleLevel,
  type OrgModuleDto,
  type BuildingSummary,
  type InvitationRow,
  type MemberRow,
  type Role,
} from "@klantpunt/shared";
import { AccessDialog, accessSummary } from "../features/org/AccessDialog";
import { useClients } from "../lib/clientContext";
import { api, ApiError } from "../lib/api";
import { useMe } from "../lib/auth";
import { useToast } from "../lib/toast";

interface MembersResponse {
  members: MemberRow[];
  invitations: InvitationRow[];
  mailEnabled: boolean;
  /** Modules van de organisatie (stand en wat ze toelaten). */
  modules: OrgModuleDto[];
}

/** Deze rollen zien alle gebouwen; voor de rest bepaalt de beheerder de toegang per gebouw. */
const SEES_ALL: readonly Role[] = ["owner", "beheerder"];

function BuildingPicker({
  buildings,
  value,
  onChange,
  legend,
}: {
  buildings: BuildingSummary[];
  value: string[];
  onChange: (ids: string[]) => void;
  legend: string;
}) {
  return (
    <fieldset className="checklist">
      <legend className="label">{legend}</legend>
      {buildings.length === 0 && <p className="muted">Nog geen gebouwen.</p>}
      {buildings.map((b) => (
        <label key={b.id}>
          <input
            type="checkbox"
            checked={value.includes(b.id)}
            onChange={(e) => onChange(e.target.checked ? [...value, b.id] : value.filter((x) => x !== b.id))}
          />
          <span>
            <span className="code">{b.code}</span> {b.name}
          </span>
        </label>
      ))}
    </fieldset>
  );
}

const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString("nl-NL", { day: "numeric", month: "short", year: "numeric" }) : "nog niet";

/** Gebruikers van de organisatie (Organisatie → Gebruikers). */
export function UsersSection() {
  const { data: me } = useMe();
  const qc = useQueryClient();
  const toast = useToast();
  const key = ["members", me?.org?.id];
  const q = useQuery({ queryKey: key, queryFn: () => api<MembersResponse>("/org/members") });
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>("inspecteur");
  const [buildingIds, setBuildingIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<MemberRow | null>(null);
  const clients = useClients();
  const buildingsQ = useQuery({
    queryKey: ["buildings", me?.org?.id],
    queryFn: () => api<BuildingSummary[]>("/buildings"),
  });
  const allBuildings = buildingsQ.data ?? [];
  const codes = (ids: string[]) =>
    ids
      .map((id) => allBuildings.find((b) => b.id === id)?.code)
      .filter(Boolean)
      .join(", ");

  const isOwner = me?.role === "owner";
  const assignable = ROLES.filter((r) => isOwner || r !== "owner");
  const onError = (e: unknown) => toast(e instanceof ApiError ? e.message : "Er ging iets mis");
  const refresh = () => qc.invalidateQueries({ queryKey: key });

  const invite = useMutation({
    mutationFn: () =>
      api<{ mailed: boolean }>("/org/invitations", {
        method: "POST",
        body: { email, role, buildingIds: SEES_ALL.includes(role) ? [] : buildingIds },
      }),
    onSuccess: (res) => {
      toast(
        res.mailed ? `Uitnodiging gestuurd naar ${email}` : `${email} toegevoegd. Laat diegene inloggen met Google.`,
      );
      setEmail("");
      setBuildingIds([]);
      setError(null);
      void refresh();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : "Toevoegen mislukt"),
  });
  const changeRole = useMutation({
    mutationFn: (v: { userId: string; role: Role }) =>
      api(`/org/members/${v.userId}`, { method: "PATCH", body: { role: v.role } }),
    onSuccess: () => {
      toast("Rol gewijzigd");
      void refresh();
    },
    onError: (e) => {
      onError(e);
      void refresh();
    },
  });
  const remove = useMutation({
    mutationFn: (userId: string) => api(`/org/members/${userId}`, { method: "DELETE" }),
    onSuccess: () => {
      toast("Gebruiker verwijderd");
      void refresh();
    },
    onError,
  });
  const resend = useMutation({
    mutationFn: (i: InvitationRow) =>
      api<{ mailed: boolean }>(`/org/invitations/${i.id}/resend`, { method: "POST" }).then((r) => ({ ...r, i })),
    onSuccess: (r) => {
      toast(r.mailed ? `Uitnodiging opnieuw gestuurd naar ${r.i.email}` : "Versturen mislukt, probeer het later");
      void refresh();
    },
    onError,
  });
  const saveModule = useMutation({
    mutationFn: (v: { userId: string; module: ModuleKey; level: ModuleLevel }) =>
      api(`/org/members/${v.userId}/modules`, { method: "PUT", body: { levels: { [v.module]: v.level } } }),
    onSuccess: () => {
      toast("Rechten bijgewerkt");
      void refresh();
    },
    onError: (e) => {
      onError(e);
      void refresh();
    },
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api(`/org/invitations/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      toast("Uitnodiging ingetrokken");
      void refresh();
    },
    onError,
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    invite.mutate();
  }

  const mail = q.data?.mailEnabled ?? false;

  return (
    <>
      <section className="panel">
        <h2>{mail ? "Uitnodigen" : "Gebruiker toevoegen"}</h2>
        <p className="muted">
          {mail
            ? "De gebruiker krijgt een mail en logt in met Google of met een inloglink."
            : "Na toevoegen kan de gebruiker inloggen met Google op dit e-mailadres (14 dagen geldig)."}
        </p>
        {error && (
          <div className="alert" role="alert">
            {error}
          </div>
        )}
        <form className="row" onSubmit={submit}>
          <label className="field">
            <span className="label">E-mailadres</span>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="naam@bedrijf.nl"
            />
          </label>
          <label className="field" style={{ flex: "0 1 12rem" }}>
            <span className="label">Rol</span>
            <select value={role} onChange={(e) => setRole(e.target.value as Role)}>
              {assignable.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABELS[r]}
                </option>
              ))}
            </select>
          </label>
          <button type="submit" className="btn primary" disabled={invite.isPending}>
            {mail ? "Uitnodigen" : "Toevoegen"}
          </button>
        </form>
        {SEES_ALL.includes(role) ? (
          <p className="muted small">{ROLE_LABELS[role]}s zien alle gebouwen.</p>
        ) : (
          <BuildingPicker
            legend="Toegang tot gebouwen"
            buildings={allBuildings}
            value={buildingIds}
            onChange={setBuildingIds}
          />
        )}
      </section>

      <section className="panel" aria-busy={q.isPending}>
        <h2>Leden</h2>
        {q.isPending && <Loading />}
        <ul className="list">
          {q.data?.members.map((m) => {
            const self = m.userId === me?.user.id;
            const locked = !isOwner && m.role === "owner";
            return (
              <li key={m.userId}>
                <span className="grow">
                  <strong>{m.name ?? m.email}</strong>
                  {m.name && <span className="muted"> · {m.email}</span>}
                  <br />
                  <span className="muted">Laatst ingelogd: {fmtDate(m.lastLoginAt)}</span>
                  <br />
                  <span className="muted">
                    Toegang: {SEES_ALL.includes(m.role) ? "alle klanten" : accessSummary(m.access, clients.data)}
                  </span>
                </span>
                {!SEES_ALL.includes(m.role) && (
                  <button
                    type="button"
                    className="btn small"
                    aria-label={`Toegang van ${m.name ?? m.email}`}
                    onClick={() => setEditing(m)}
                  >
                    Toegang
                  </button>
                )}
                <label>
                  <span className="sr-only">Rol van {m.name ?? m.email}</span>
                  <select
                    value={m.role}
                    disabled={locked || changeRole.isPending}
                    onChange={(e) => changeRole.mutate({ userId: m.userId, role: e.target.value as Role })}
                  >
                    {(locked ? ROLES : assignable).map((r) => (
                      <option key={r} value={r}>
                        {ROLE_LABELS[r]}
                      </option>
                    ))}
                  </select>
                </label>
                {!self && (
                  <button
                    type="button"
                    className="btn small danger"
                    disabled={locked}
                    onClick={() => {
                      if (confirm(`${m.name ?? m.email} verwijderen uit ${me?.org?.name}?`)) remove.mutate(m.userId);
                    }}
                  >
                    Verwijderen
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      </section>

      {q.data && (
        <section className="panel" aria-labelledby="rights-h">
          <h2 id="rights-h">Rechten per module</h2>
          <p className="muted small">
            Per gebruiker per module: geen, bekijken of bewerken. Eigenaars en beheerders hebben altijd bewerken.
            Modules die de organisatie niet heeft, staan grijs.
          </p>
          <div className="tscroll">
            <table className="it rights">
              <thead>
                <tr>
                  <th>Gebruiker</th>
                  {q.data.modules.map((m) => (
                    <th key={m.module} className={m.cap === "none" ? "muted" : undefined}>
                      {MODULE_LABELS[m.module]}
                      {m.status !== "active" && (
                        <span className="muted small block">
                          {m.cap === "view" ? "proef verlopen" : ORG_MODULE_STATUS_LABELS[m.status].toLowerCase()}
                        </span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {q.data.members.map((u) => (
                  <tr key={u.userId}>
                    <td>
                      {u.name ?? u.email}
                      <span className="muted small block">{ROLE_LABELS[u.role]}</span>
                    </td>
                    {q.data.modules.map((m) => (
                      <td key={m.module}>
                        {ALWAYS_EDIT_ROLES.has(u.role) ? (
                          <span className="muted small">Bewerken</span>
                        ) : (
                          <select
                            aria-label={`${MODULE_LABELS[m.module]} voor ${u.name ?? u.email}`}
                            value={u.modules[m.module]}
                            disabled={m.cap === "none" || saveModule.isPending}
                            onChange={(e) =>
                              saveModule.mutate({
                                userId: u.userId,
                                module: m.module,
                                level: e.target.value as ModuleLevel,
                              })
                            }
                          >
                            {MODULE_LEVELS.map((l) => (
                              <option key={l} value={l}>
                                {MODULE_LEVEL_LABELS[l]}
                              </option>
                            ))}
                          </select>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {q.data && q.data.invitations.length > 0 && (
        <section className="panel">
          <h2>Nog niet ingelogd</h2>
          <ul className="list">
            {q.data.invitations.map((i) => (
              <li key={i.id}>
                <span className="grow">
                  <strong>{i.email}</strong>
                  <br />
                  <span className="muted">
                    {ROLE_LABELS[i.role]} · geldig tot {fmtDate(i.expiresAt)}
                    {i.buildingIds.length > 0 && ` · ${codes(i.buildingIds)}`}
                  </span>
                </span>
                {mail && (
                  <button
                    type="button"
                    className="btn small"
                    disabled={resend.isPending || (!isOwner && i.role === "owner")}
                    onClick={() => resend.mutate(i)}
                  >
                    Opnieuw sturen
                  </button>
                )}
                <button
                  type="button"
                  className="btn small"
                  disabled={!isOwner && i.role === "owner"}
                  onClick={() => revoke.mutate(i.id)}
                >
                  Intrekken
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {editing && (
        <AccessDialog
          member={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void refresh();
          }}
        />
      )}
    </>
  );
}
